import React from "react";
import {
    Box,
    Card,
    Stack,
    CardContent,
    Typography,
    Grid,
    Container
} from "@mui/material";
import { useTranslation } from "react-i18next";
import DateRangeSelector from "../../components/DateRangeSelector";
import GlobalMetrics from "./components/GlobalMetrics";
import FacilityCard from "./components/FacilityCard";

import { ApiContext } from '../../App';
import PortfolioSavingsChart from "./components/PortfolioSavingsChart";
import { useSearchParams } from "react-router-dom";


const EMPTY_TOTAL_METRICS = { "kwh": 0, "co2": 0, "cost": 0 };

export default function Savings({ clientInfo, facility, facilities }) {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();


    const [fromDate, setFromDate] = React.useState(() => {
        const date = new Date();
        date.setDate(date.getDate() - 365);
        return date;
    });
    const [toDate, setToDate] = React.useState(new Date());

    const [totalMetrics, setTotalMetrics] = React.useState(EMPTY_TOTAL_METRICS);
    const [facilityMetrics, setFacilityMetrics] = React.useState([]);

    const { hvacApi } = React.useContext(ApiContext);

    const handleAPIError = () => {
        window.alert(t("Couldn't retrieve data."));
    }

    React.useEffect(() => {

        if (fromDate <= toDate) {
            hvacApi.getPerformanceReport(
                fromDate,
                toDate,
                data => {
                    setTotalMetrics(data.totals);
                    setFacilityMetrics(data.breakdown);
                },
                () => {
                    handleAPIError();
                },
            )
        } else {
            setTotalMetrics(EMPTY_TOTAL_METRICS);
            setFacilityMetrics([])
        }

    },
        [fromDate, toDate]
    )

    const calculateSavings = (metrics) => {

        const kwhBaseline = (metrics.baseline?.kwh || 0);
        const kwhActual = (metrics.actual?.kwh || 0);

        const co2Baseline = (metrics.baseline?.co2 || 0);
        const co2Actual = (metrics.actual?.co2 || 0);

        const costBaseline = (metrics.baseline?.cost || 0);
        const costActual = (metrics.actual?.cost || 0);

        return {
            kwh: kwhBaseline - kwhActual,
            co2: co2Baseline - co2Actual,
            cost: costBaseline - costActual,
            kwhPercent: (kwhBaseline - kwhActual) / kwhBaseline * 100.0 || 0.0,
            co2Percent: (co2Baseline - co2Actual) / co2Baseline * 100.0 || 0.0,
            costPercent: (costBaseline - costActual) / costBaseline * 100.0 || 0.0
        };
    };

    const calculateFacilitySavings = (fac) => {

        const facilityMeta = facilities.filter((item) => item.name === fac.facility_name)[0]
        return {
            ...calculateSavings(fac),
            ...facilityMeta
        };
    };

    return (
            <Box
                sx={{
                    backgroundColor: (theme) =>
                        theme.palette.mode === "light"
                            ? theme.palette.grey[100]
                            : theme.palette.grey[900],
                    p: 3,
                    minHeight: "calc(100vh - 65px)"
                }}
            >
                <Container>
                    <Stack spacing={2} mb={2}>
                        <Card>
                            <CardContent>
                                <DateRangeSelector
                                    fromDate={fromDate}
                                    toDate={toDate}
                                    onFromDateChange={(newDate) => setFromDate(newDate)}
                                    onToDateChange={(newDate) => setToDate(newDate)}
                                    small={true}
                                    hideTitle
                                />
                            </CardContent>
                        </Card>
                        <GlobalMetrics metrics={calculateSavings(totalMetrics)} currencySymbol={clientInfo.currency_symbol} />

                        <PortfolioSavingsChart startDate={fromDate} endDate={toDate} localTimeZone={clientInfo.time_zone} currencySymbol={clientInfo.currency_symbol} />
                    </Stack>

                    <Grid container spacing={2} justifyContent="left">
                        {facilityMetrics.length > 0 ? (
                            facilityMetrics.map((fac, index) => (
                                <Grid item sm={12} md={6} key={index}>
                                    <FacilityCard facility={calculateFacilitySavings(fac)} currencySymbol={clientInfo.currency_symbol} />
                                </Grid>
                            ))
                        ) : null}
                    </Grid>
                </Container>
            </Box>
    );
}
